<script setup>
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import SalesReviewCard from './SalesReviewCard.vue';
import SalesReviewReturnComposer from './SalesReviewReturnComposer.vue';
import SalesReviewReturnConfirmation from './SalesReviewReturnConfirmation.vue';
import SalesReviewReturnInfo from './SalesReviewReturnInfo.vue';
import SalesReviewResubmitConfirmation from './SalesReviewResubmitConfirmation.vue';

defineProps({
  canCompose: { type: Boolean, default: false },
  canSubmit: { type: Boolean, default: false },
  catalog: { type: Object, default: () => ({}) },
  errors: { type: Object, default: () => ({}) },
  isCorrection: { type: Boolean, default: false },
  items: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  participants: { type: Array, default: () => [] },
  saving: { type: Boolean, default: false },
  showConfirmation: { type: Boolean, default: false },
  showResubmitConfirmation: { type: Boolean, default: false },
  transaction: { type: Object, default: null },
});
const emit = defineEmits([
  'closeConfirmation',
  'closeResubmitConfirmation',
  'confirmReturn',
  'confirmResubmit',
  'openItem',
  'removeItem',
  'replaceItems',
  'submit',
]);
const { t } = useI18n();
</script>

<template>
  <main
    class="mx-auto flex w-full max-w-[1500px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
  >
    <div
      v-if="loading"
      class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
    >
      {{ t('SALES.COMMON.LOADING') }}
    </div>
    <template v-else-if="transaction">
      <SalesReviewReturnInfo v-if="!isCorrection" />
      <LaisCard
        v-if="isCorrection"
        :title="transaction.title"
        prefix-icon="i-lucide-house"
      >
        <p class="text-sm text-lais-text-muted">
          {{
            transaction.address?.endereco ||
            t('SALES.COMMON.ADDRESS_UNAVAILABLE')
          }}
        </p>
      </LaisCard>
      <SalesReviewReturnComposer
        v-if="!isCorrection"
        :catalog="catalog"
        :items="items"
        :participants="participants"
        :read-only="!canCompose"
        :show-errors="Boolean(errors.items)"
        @update:items="emit('replaceItems', $event)"
      />
      <p v-if="errors.items" class="text-sm text-lais-danger">
        {{ errors.items }}
      </p>
      <section v-if="isCorrection" class="grid gap-4 lg:grid-cols-2">
        <SalesReviewCard
          v-for="(item, index) in items"
          :key="item.id || `${item.section}-${item.target_key}-${index}`"
          :item="item"
          :index="index"
          :editable="canCompose"
          :participants="participants"
          @open="emit('openItem', $event)"
          @remove="emit('removeItem', $event)"
        />
      </section>
      <footer
        v-if="canSubmit && !isCorrection"
        class="sticky bottom-0 z-10 flex justify-end rounded-lais border border-lais-border-soft bg-white/95 p-4 shadow-lais-soft backdrop-blur"
      >
        <LaisPrimaryButton
          size="lg"
          icon="i-lucide-arrow-right"
          :is-loading="saving"
          @click="emit('submit')"
        >
          {{ t('SALES.REVIEW.ACTIONS.REQUEST') }}
        </LaisPrimaryButton>
      </footer>
    </template>
  </main>

  <SalesReviewReturnConfirmation
    :show="showConfirmation"
    @close="emit('closeConfirmation')"
    @confirm="emit('confirmReturn')"
  />
  <SalesReviewResubmitConfirmation
    :saving="saving"
    :show="showResubmitConfirmation"
    @close="emit('closeResubmitConfirmation')"
    @confirm="emit('confirmResubmit')"
  />
</template>
